import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
import RoleBasedGuard from 'src/auth/guard/role-based-guard';

import { RouteGuard } from 'src/components/permissions';
import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

const ConfigurationMainView = lazy(
  () => import('src/pages/medicore/configuration/configuration-main-view')
);

// Equipment Category pages
const EquipmentCategoryListView = lazy(
  () => import('src/pages/medicore/configuration/equipment-category/equipment-category-list-view')
);
const EquipmentCategoryNewView = lazy(
  () => import('src/pages/medicore/configuration/equipment-category/equipment-category-new-view')
);
const EquipmentCategoryEditView = lazy(
  () => import('src/pages/medicore/configuration/equipment-category/equipment-category-edit-view')
);

// Doctor Fees Catalog pages
const DoctorFeesCatalogListView = lazy(
  () => import('src/pages/medicore/configuration/doctor-fees-catalog/doctor-fees-catalog-list-view')
);
// Doctor Services Catalog pages
const DoctorServicesCatalogListView = lazy(
  () =>
    import('src/pages/medicore/configuration/doctor-services-catalog/doctor-services-catalog-list-view')
);

// Doctor Speciality pages
const DoctorSpecialityListView = lazy(
  () => import('src/pages/medicore/configuration/doctor-speciality/doctor-speciality-list-view')
);

// Dynamic Accounting pages
const DynamicAccountingMainView = lazy(
  () => import('src/pages/medicore/configuration/dynamic-accounting/dynamic-accounting-main-view')
);
const EntityMappingsListView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/entity-mappings/entity-mappings-list-view')
);
const EntityMappingsNewView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/entity-mappings/entity-mappings-new-view')
);
const EntityMappingsEditView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/entity-mappings/entity-mappings-edit-view')
);
const JournalTemplatesListView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/journal-templates/journal-templates-list-view')
);
const JournalTemplatesNewView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/journal-templates/journal-templates-new-view')
);
const JournalTemplatesEditView = lazy(
  () =>
    import('src/pages/medicore/configuration/dynamic-accounting/journal-templates/journal-templates-edit-view')
);

// Insurance Templates pages
const InsuranceTemplatesListView = lazy(
  () => import('src/pages/medicore/configuration/insurance-templates/insurance-templates-list-view')
);
const InsuranceTemplatesMatchingView = lazy(
  () =>
    import('src/pages/medicore/configuration/insurance-templates/insurance-templates-matching-view')
);
const InsuranceTemplatesDesignerView = lazy(
  () =>
    import('src/pages/medicore/configuration/insurance-templates/insurance-templates-designer-view')
);

const TaxesView = lazy(() => import('src/pages/medicore/configuration/Taxes-View/Taxes'));

// System Parameters
const SystemParametersPage = lazy(
  () => import('src/pages/medicore/configuration/system-parameters/system-parameters-page')
);

// Device Maintenance
const DeviceMaintenancePage = lazy(
  () => import('src/pages/medicore/configuration/device-maintenance/device-maintenance-page')
);

// Membrete del centro emisor
const CentroMembretePage = lazy(
  () => import('src/pages/medicore/configuration/centro-membrete/centro-membrete-page')
);

// Catálogo clínico de medicamentos (el vademécum de la receta)
const MedicamentoCatalogoPage = lazy(
  () => import('src/pages/medicore/configuration/medicamento-catalogo/medicamento-catalogo-page')
);

// ----------------------------------------------------------------------

/**
 * Componente wrapper para proteger rutas de configuración con permisos específicos
 * ⚠️ ULTRA-RESTRICTIVO: Solo master ve todo, otros usuarios solo lo que tienen permisos
 */
const ProtectedRoute = ({ route, children }) => (
  <RouteGuard route={route} fallbackRoute="/medicore" showFallback>
    <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
  </RouteGuard>
);

ProtectedRoute.propTypes = {
  route: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ----------------------------------------------------------------------

export const configurationRoutes = [
  {
    path: 'medicore/configuration',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['doctor', 'employee']}>
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      {
        element: (
          <ProtectedRoute route="/medicore/configuration/root">
            <ConfigurationMainView />
          </ProtectedRoute>
        ),
        index: true,
      },
      {
        path: 'equipment-category',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/equipmentCategory/list">
                <EquipmentCategoryListView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/equipmentCategory/list">
                <EquipmentCategoryListView />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/configuration/equipmentCategory/list">
                <EquipmentCategoryNewView />
              </ProtectedRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <ProtectedRoute route="/medicore/configuration/equipmentCategory/list">
                <EquipmentCategoryEditView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'doctor-fees-catalog',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorFeesCatalog/list">
                <DoctorFeesCatalogListView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorFeesCatalog/list">
                <DoctorFeesCatalogListView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'doctor-services-catalog',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorServicesCatalog/list">
                <DoctorServicesCatalogListView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorServicesCatalog/list">
                <DoctorServicesCatalogListView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'doctor-speciality',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorSpeciality/list">
                <DoctorSpecialityListView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/doctorSpeciality/list">
                <DoctorSpecialityListView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'dynamic-accounting',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                <DynamicAccountingMainView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                <DynamicAccountingMainView />
              </ProtectedRoute>
            ),
          },
          {
            path: 'entity-mappings',
            children: [
              {
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <EntityMappingsListView />
                  </ProtectedRoute>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <EntityMappingsListView />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'new',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <EntityMappingsNewView />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <EntityMappingsEditView />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'journal-templates',
            children: [
              {
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <JournalTemplatesListView />
                  </ProtectedRoute>
                ),
                index: true,
              },
              {
                path: 'list',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <JournalTemplatesListView />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'new',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <JournalTemplatesNewView />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <ProtectedRoute route="/medicore/configuration/dynamicAccounting/root">
                    <JournalTemplatesEditView />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'insurance-templates',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/configuration/insuranceTemplates/list">
                <InsuranceTemplatesListView />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/configuration/insuranceTemplates/list">
                <InsuranceTemplatesListView />
              </ProtectedRoute>
            ),
          },
          {
            path: 'matching',
            element: (
              <ProtectedRoute route="/medicore/configuration/insuranceTemplates/list">
                <InsuranceTemplatesMatchingView />
              </ProtectedRoute>
            ),
          },
          {
            path: 'designer/:id',
            element: (
              <ProtectedRoute route="/medicore/configuration/insuranceTemplates/list">
                <InsuranceTemplatesDesignerView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'taxes',
        element: (
          <ProtectedRoute route="/medicore/configuration/taxes">
            <TaxesView />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system-parameters',
        element: (
          <ProtectedRoute route="/medicore/configuration/systemParameters">
            <SystemParametersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'device-maintenance',
        element: (
          <ProtectedRoute route="/medicore/configuration/device-maintenance">
            <DeviceMaintenancePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'centro-membrete',
        element: (
          <ProtectedRoute route="/medicore/configuration/centro-membrete">
            <CentroMembretePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'medicamento-catalogo',
        element: (
          <ProtectedRoute route="/medicore/configuration/medicamento-catalogo">
            <MedicamentoCatalogoPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
];
